<script lang="ts">
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import ThemeToggle from '$lib/components/ThemeToggle.svelte';
	import { deviceScreen } from '$lib/state/deviceScreen.svelte';
	import { useBackButtonInterception } from '$lib/utils/history.util.svelte';
	import { UrlUtil, type NavLink } from '$lib/utils/url.util';
	import { fade, fly } from 'svelte/transition';
	import IconClose from '~icons/mdi/close';
	import IconDice from '~icons/mdi/dice';
	import IconMenu from '~icons/mdi/menu';
	import IconSearch from '~icons/mdi/search';

	const navItems: { label: string; link: NavLink }[] = [
		{ label: 'Cities', link: UrlUtil.toCities() },
		{ label: 'Countries', link: UrlUtil.toCountries() },
		{ label: 'Explore', link: UrlUtil.toAll() },
		{ label: 'Atlas', link: UrlUtil.toAtlas() }
	];

	const { isOverlay = false }: { isOverlay: boolean } = $props();

	let scrollY = $state<number>(0);
	let searchQuery = $state<string>('');
	let isSearchFocused = $state<boolean>(false);
	let isDrawerOpen = $state<boolean>(false);

	// Logic:
	// If it's an Overlay page (e.g. has a big hero image), we only show background when scrolled > 20px.
	// If it's a Standard page (!isOverlay), we ALWAYS show background.
	const showBackground = $derived<boolean>(!isOverlay || scrollY > 20 || isDrawerOpen);

	// Logic:
	// Overlay pages start with white text (on dark hero images).
	// Standard pages use default text color immediately.
	const textColorClass = $derived<string>(isOverlay && !showBackground && !isDrawerOpen ? 'text-white' : 'text-base-content');

	// Logic:
	// Overlay = 'fixed' (Content slides underneath)
	// Standard = 'sticky' (Content is pushed down, avoiding overlap)
	const positionClass = $derived<string>(isOverlay ? 'fixed top-0' : 'sticky top-0');

	const iconButtonClass = $derived<string>(
		[
			'flex size-10 items-center justify-center rounded-full transition-all duration-200',
			showBackground
				? 'text-base-content/70 hover:bg-base-content/10 hover:text-primary'
				: 'text-white/80 hover:bg-white/15 hover:text-white hover:backdrop-blur-md'
		].join(' ')
	);

	useBackButtonInterception({
		stateKey: 'rootSideNavbar',
		active: () => isDrawerOpen,
		enabled: () => deviceScreen.smallerThanDesktop,
		onBack: closeDrawer,
		onForward: openDrawer
	});

	function handleFocus() {
		isSearchFocused = true;
	}

	function handleBlur() {
		// Small timeout to allow button click to register before hiding
		setTimeout(() => {
			isSearchFocused = false;
		}, 200);
	}

	function openDrawer() {
		isDrawerOpen = true;
	}

	function closeDrawer() {
		isDrawerOpen = false;
	}
</script>

<svelte:window bind:scrollY />

<div
	id="navbar"
	class={[
		'z-50 w-full border-b transition-all duration-300',
		positionClass,
		showBackground ? 'border-base-content/5 bg-base-100/80 shadow-sm backdrop-blur-md' : 'border-transparent bg-transparent',
		textColorClass
	]}
>
	<div class="navbar container mx-auto px-4">
		<div class="navbar-start">
			{@render MobileMenuTrigger()}
			{@render BrandLogo()}
		</div>

		<div class="navbar-center hidden lg:flex">
			<ul class="menu menu-horizontal px-1">
				{@render NavLinks()}
			</ul>
		</div>

		<div class="navbar-end flex flex-row items-center gap-5">
			<div class="tooltip tooltip-bottom hidden tooltip-accent md:flex" data-tip="Toggle Theme">
				<div class={[iconButtonClass, 'cursor-pointer']}>
					<ThemeToggle />
				</div>
			</div>

			<div class="tooltip tooltip-bottom tooltip-accent md:flex" data-tip="Random City">
				<button
					class={[iconButtonClass, 'hidden cursor-pointer md:flex']}
					aria-label="Random City"
					title="Random City"
					onclick={() => goto(`/${UrlUtil.toRandomCity().href}`, { state: { isRandomCityNavigation: true } })}
				>
					<IconDice class="text-xl" />
				</button>
			</div>

			{@render SearchInput()}
		</div>
	</div>
</div>

{@render MobileDrawer()}

{#snippet BrandLogo()}
	<a class="text-2xl font-bold tracking-tighter transition-opacity hover:opacity-80" href="/" onclick={closeDrawer}> Velatlas </a>
{/snippet}

{#snippet MobileMenuTrigger()}
	<button class={[iconButtonClass, 'mr-2 lg:hidden']} aria-label="Open Menu" onclick={openDrawer}>
		<IconMenu class="text-xl" />
	</button>
{/snippet}

{#snippet MobileDrawer()}
	{#if isDrawerOpen}
		{@render Backdrop()}

		<aside
			class="fixed top-0 bottom-0 left-0 z-70 flex w-[80%] max-w-sm flex-col bg-base-100 text-base-content shadow-2xl lg:hidden"
			transition:fly={{ x: -400, duration: 300, opacity: 1 }}
		>
			<div class="flex items-center justify-end gap-2 border-b border-base-200 p-4">
				<div class={[iconButtonClass, 'size-10 shrink-0']}>
					<ThemeToggle />
				</div>
				<button class={iconButtonClass} onclick={closeDrawer} aria-label="Close Menu">
					<IconClose class="text-xl" />
				</button>
			</div>

			<nav class="flex-1 overflow-y-auto p-4">
				<ul class="menu w-full space-y-2 menu-lg text-base-content">
					{@render NavLinks()}

					<li class="mt-2 border-t border-base-content/10 menu-title pt-4">Explore</li>

					<li>
						<button
							class="flex cursor-pointer items-center gap-3 border-none bg-transparent p-0 text-left font-medium text-base-content"
							onclick={() => {
								closeDrawer();
								goto(`/${UrlUtil.toRandomCity().href}`, { state: { isRandomCityNavigation: true } });
							}}
						>
							<IconDice class="text-lg opacity-70" />
							Random City
						</button>
					</li>
				</ul>
			</nav>

			<div class="border-t border-base-200 p-4 text-center">
				{@render BrandLogo()}
			</div>
		</aside>
	{/if}

	{#snippet Backdrop()}
		<div
			class="fixed inset-0 z-60 bg-black/40 backdrop-blur-sm lg:hidden"
			transition:fade={{ duration: 200 }}
			onclick={closeDrawer}
			role="button"
			tabindex="-1"
			aria-label="Close menu"
			onkeydown={() => {}}
		></div>
	{/snippet}
{/snippet}

{#snippet NavLinks()}
	{#each navItems as item (item.label)}
		{const isPageActive = $derived<boolean>(UrlUtil.isActive({ currentRouteId: page.route.id, link: item.link }))}
		<li>
			<a
				class={{
					'transition-all duration-100': true,

					'bg-primary/15 font-bold text-primary': isPageActive && showBackground,
					'bg-white/25 font-bold text-white shadow-sm backdrop-blur-md': isPageActive && !showBackground,

					'font-medium hover:bg-base-content/10 hover:text-primary': !isPageActive && showBackground,
					'font-medium text-white/80 hover:bg-white/15 hover:text-white': !isPageActive && !showBackground
				}}
				href={`/${item.link.href}`}
				data-sveltekit-preload-data={item.link.preloadData}
				data-sveltekit-preload-code={item.link.preloadCode}
				onclick={closeDrawer}
			>
				{item.label}
			</a>
		</li>
	{/each}
{/snippet}

{#snippet SearchInput()}
	<form class="relative flex items-center justify-end" action="/{UrlUtil.toSearch().href}" role="search">
		<input
			class={{
				'input w-full max-w-48 rounded-full pr-10 transition-all duration-300 input-md focus:max-w-56 focus:outline-none sm:max-w-[16rem] sm:focus:max-w-[20rem] md:max-w-[20rem] md:focus:max-w-100 ': true,
				'border-white/20 bg-white/10 text-white placeholder-white/60 focus:bg-base-100 focus:text-base-content focus:placeholder:text-base-content/70':
					!showBackground,
				'border-transparent bg-base-200 text-base-content placeholder-base-content/60 focus:bg-base-100 focus:ring-1 focus:ring-base-content/20':
					showBackground
			}}
			name="q"
			type="search"
			aria-label="Search cities and countries"
			placeholder="Where to?"
			bind:value={searchQuery}
			onfocus={handleFocus}
			onblur={handleBlur}
			required
		/>

		{#if isSearchFocused || searchQuery.length > 0}
			<div class="absolute top-0 right-0 bottom-0 flex items-center pr-1">
				<button
					type="submit"
					transition:fade={{ duration: 200 }}
					class="btn btn-circle border-none shadow-md btn-primary btn-sm"
					aria-label="Search"
				>
					<IconSearch />
				</button>
			</div>
		{/if}
	</form>
{/snippet}
